import React, { useState } from 'react';
import { Download, Eye, ReceiptText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Toast } from '../../../../components/design-system/common/Toast';
import { PdfViewerModal } from '../../../../common/components/pdf-viewer-modal';
import { useBillingApi } from '../../../../common/hooks/use-billing-api.hook';
import { VendorInvoice, StripeInvoiceSummary, invoiceLines } from '../../../../common/models/billing.model';

// Status colouring per invoice status — covers both wire (pending/paid/void/expired)
// and Stripe (paid/open/void/uncollectible) statuses, with a neutral fallback.
const STATUS_CLASS: Record<string, string> = {
  pending: 'text-warning',
  open: 'text-warning',
  paid: 'text-success',
  void: 'text-muted-foreground',
  expired: 'text-destructive',
  uncollectible: 'text-destructive',
};
const STATUS_FALLBACK: Record<string, string> = {
  pending: 'Awaiting payment', open: 'Open', paid: 'Paid', void: 'Void', expired: 'Expired', uncollectible: 'Uncollectible',
};

const money = (amount: number, currency: string): string => {
  const symbol = (currency || 'usd').toLowerCase() === 'usd' ? '$' : `${(currency || '').toUpperCase()} `;
  return `${symbol}${(amount ?? 0).toLocaleString()}`;
};
const fmtDate = (iso?: string | null): string => (iso ? new Date(iso).toLocaleDateString() : '—');

// A normalised row for the merged history table.
interface HistoryRow {
  key: string;
  source: 'wire' | 'stripe';
  id: string;
  number: string;
  planLine: string;
  /** One-time charges folded into this invoice's total, named so it reconciles. */
  subLine?: string;
  amount: number;
  currency: string;
  date?: string | null;
  status: string;
  pdfUrl?: string | null;
  hostedUrl?: string | null;
}

interface BillingHistoryTableProps {
  invoices: VendorInvoice[] | null; // null = still loading (parent controls)
  stripeInvoices?: StripeInvoiceSummary[]; // Stripe (card) invoices, merged in
}

/**
 * The "Billing History" table: one row per invoice — wire-transfer invoices
 * (rendered by our own PDF service) and Stripe card invoices (linked to Stripe's
 * hosted PDF), merged and sorted by date. Loading/empty states are driven by the
 * parent passing `null`/`[]` for the wire invoices.
 */
export const BillingHistoryTable: React.FC<BillingHistoryTableProps> = ({ invoices, stripeInvoices = [] }) => {
  const { t } = useTranslation();
  const billingApi = useBillingApi();

  const [error, setError] = useState(false);
  const [viewer, setViewer] = useState<{ id: string; number: string; url: string | null } | null>(null);

  const openViewer = async (id: string, number: string) => {
    setViewer({ id, number, url: null });
    try {
      const url = await billingApi.viewInvoicePdf(id);
      setViewer((v) => (v && v.id === id ? { id, number, url } : v));
    } catch {
      setViewer(null);
      setError(true);
    }
  };
  const closeViewer = () => setViewer((v) => { if (v?.url) window.URL.revokeObjectURL(v.url); return null; });

  const intervalLabel = (interval?: string) => (interval === 'year' ? t('BILLING.ANNUAL', 'Annual') : t('BILLING.MONTHLY', 'Monthly'));

  const wireRows: HistoryRow[] = (invoices || []).map((inv) => {
    const oneTime = invoiceLines(inv).filter((l) => !l.recurring);
    // A fee invoice buys no plan, so naming a plan + interval on it would be a
    // lie — it is described by its own charges instead.
    const planLine = inv.type === 'fee'
      ? oneTime.map((l) => l.description).join(' · ') || t('BILLING.ONE_TIME', 'One-time charge')
      : `${inv.planName} · ${intervalLabel(inv.interval)}${inv.type !== 'initial' && inv.type !== 'historical' ? ` · ${t(`BILLING.TYPE.${inv.type}`, inv.type)}` : ''}`;
    return {
      key: `w_${inv._id}`,
      source: 'wire' as const,
      id: inv._id,
      number: inv.invoiceNumber,
      planLine,
      subLine: inv.type !== 'fee' && oneTime.length
        ? oneTime.map((l) => `${l.description} ${money(l.amount, inv.currency)}`).join(' · ')
        : undefined,
      amount: inv.amount,
      currency: inv.currency,
      date: inv.issuedAt,
      status: inv.status,
    };
  });
  const stripeRows: HistoryRow[] = (stripeInvoices || []).map((inv) => ({
    key: `s_${inv.id}`,
    source: 'stripe',
    id: inv.id,
    number: inv.invoiceNumber,
    planLine: `${inv.planName || t('BILLING.SUBSCRIPTION', 'Subscription')} · ${intervalLabel(inv.interval)}`,
    subLine: inv.oneTimeLines?.length
      ? inv.oneTimeLines.map((l) => `${l.description} ${money(l.amount, inv.currency)}`).join(' · ')
      : undefined,
    amount: inv.amount,
    currency: inv.currency,
    date: inv.issuedAt,
    status: inv.status,
    pdfUrl: inv.pdfUrl,
    hostedUrl: inv.hostedUrl,
  }));
  const rows = [...wireRows, ...stripeRows].sort(
    (a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime(),
  );

  return (
    <div>
      <h2 className="font-heading mb-4 text-lg font-medium tracking-tight">
        {t('BILLING.HISTORY_TITLE', 'Billing History')}
      </h2>

      <div data-testid="billing-history" className="overflow-hidden rounded-xl border bg-card">
        {invoices === null ? (
          <div className="p-6 text-center">
            <p className="text-sm text-muted-foreground">{t('COMMON.LOADING', 'Loading…')}</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-14 text-center">
            <ReceiptText className="mx-auto mb-2 size-8 text-muted-foreground" />
            <p className="mb-1 text-lg font-medium">{t('BILLING.NO_INVOICES_TITLE', 'No invoices yet')}</p>
            <p className="text-sm text-muted-foreground">
              {t('BILLING.NO_INVOICES_BODY', 'Invoices appear here when you pay for a plan by bank transfer.')}
            </p>
          </div>
        ) : (
          <TooltipProvider>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="ps-6 text-xs font-medium tracking-wider text-muted-foreground uppercase">{t('BILLING.COL_INVOICE', 'Invoice')}</TableHead>
                  <TableHead className="text-xs font-medium tracking-wider text-muted-foreground uppercase">{t('BILLING.COL_AMOUNT', 'Amount')}</TableHead>
                  <TableHead className="hidden text-xs font-medium tracking-wider text-muted-foreground uppercase sm:table-cell">{t('BILLING.COL_DATE', 'Date')}</TableHead>
                  <TableHead className="hidden text-xs font-medium tracking-wider text-muted-foreground uppercase md:table-cell">{t('BILLING.COL_STATUS', 'Status')}</TableHead>
                  <TableHead className="pe-6 text-end" aria-label={t('BILLING.COL_ACTIONS', 'Actions')} />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell className="ps-6">
                      <p className="text-sm font-medium">{row.number}</p>
                      <p className="text-sm text-muted-foreground">{row.planLine}</p>
                      {row.subLine && <p className="text-xs text-muted-foreground">{row.subLine}</p>}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-medium">{money(row.amount, row.currency)}</span>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <span className="text-sm text-muted-foreground">{fmtDate(row.date)}</span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Badge variant="outline" className={cn(STATUS_CLASS[row.status] || 'text-muted-foreground')}>
                        {t(`BILLING.STATUS.${row.status}`, STATUS_FALLBACK[row.status] || row.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="pe-6 text-end whitespace-nowrap">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon-sm"
                            variant="ghost"
                            aria-label={`View ${row.number}`}
                            onClick={() => (row.source === 'stripe' ? window.open(row.hostedUrl || row.pdfUrl || '', '_blank', 'noopener') : openViewer(row.id, row.number))}
                          >
                            <Eye className="text-muted-foreground" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('BILLING.VIEW_PDF', 'View PDF')}</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon-sm"
                            variant="ghost"
                            aria-label={`Download ${row.number}`}
                            onClick={() => (row.source === 'stripe'
                              ? window.open(row.pdfUrl || row.hostedUrl || '', '_blank', 'noopener')
                              : billingApi.downloadInvoicePdf(row.id, row.number).catch(() => setError(true)))}
                          >
                            <Download className="text-muted-foreground" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('BILLING.DOWNLOAD_PDF', 'Download PDF')}</TooltipContent>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TooltipProvider>
        )}
      </div>

      <PdfViewerModal
        url={viewer?.url ?? null}
        loading={!!viewer && !viewer.url}
        title={viewer ? `${t('SIDEBAR.INVOICES', 'Invoice')} ${viewer.number}` : 'Invoice'}
        onDownload={viewer ? () => billingApi.downloadInvoicePdf(viewer.id, viewer.number).catch(() => setError(true)) : undefined}
        onClose={closeViewer}
      />

      <Toast
        message={t('BILLING.PDF_ERROR', 'Could not open the PDF. Please try again.')}
        type="error"
        isVisible={error}
        onClose={() => setError(false)}
      />
    </div>
  );
};

export default BillingHistoryTable;
